import { useEffect, useState } from "react";
import { getIncidents } from "../../api/incidents";
import { useNavigate } from "react-router-dom";
import { getLang } from "../../store/uiStore";
import { getUser } from "../../store/authStore";
import "./IncidentsList.css";

export default function IncidentsList() {
  const [data, setData] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const lang = getLang();

  const user = getUser();

  const isInspector = [
    "regional_inspector",
    "clinic_inspector",
    "central",
  ].includes(user?.role);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const res = await getIncidents();

    setData(
      [...res.data].sort(
        (a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
      )
    );
  };

  const TYPE_LABELS = {
    medical_error:
      lang === "ru"
        ? "Медицинская ошибка"
        : "Хатои тиббӣ",

    infection:
      lang === "ru"
        ? "Опасное инфекционное заболевание"
        : "Бемории хавфноки сироятӣ",

    adverse_event:
      lang === "ru"
        ? "Нежелательное явление"
        : "Ҳодисаи номатлуб",
  };

  const STATUS_LABELS = {
    draft: lang === "ru" ? "Черновик" : "Лоиҳа",
    sent: lang === "ru" ? "Отправлена" : "Фиристода шуд",
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString(
      lang === "ru" ? "ru-RU" : "tg-TJ"
    );
  };

  const filteredData = data.filter((item) => {
    const matchesType =
      !typeFilter || item.type === typeFilter;

    const matchesStatus =
      !statusFilter || item.status === statusFilter;

    const query = search.trim().toLowerCase();

    if (!query) {
      return matchesType && matchesStatus;
    }

    const typeText = TYPE_LABELS[item.type] || "";
    const clinicText =
      item.clinic_name || item.clinic || "";
    const dateText = formatDate(
      item.created_at || item.createdAt
    );

    const matchesSearch = [
      typeText,
      clinicText,
      dateText,
      item.id,
    ].some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(query)
    );

    return matchesType && matchesStatus && matchesSearch;
  });

  const clearFilters = () => {
    setTypeFilter("");
    setStatusFilter("");
    setSearch("");
  };

  const hasFilters =
    Boolean(typeFilter || statusFilter || search.trim());

  const tr = {
    title:
      lang === "ru"
        ? "Экстренные извещения"
        : "Хабарномаҳои фаврӣ",

    create:
      lang === "ru"
        ? "Создать новое извещение"
        : "Сохтани хабарномаи нав",

    type:
      lang === "ru"
        ? "Тип извещения"
        : "Навъи хабарнома",

    clinic:
      lang === "ru"
        ? "Название клиники"
        : "Номи муассиса",

    date:
      lang === "ru"
        ? "Дата извещения"
        : "Санаи хабарнома",

    status:
      lang === "ru"
        ? "Статус"
        : "Ҳолат",

    empty:
      lang === "ru"
        ? "Извещений пока нет"
        : "Ҳоло хабарномаҳо вуҷуд надоранд",

    noClinic:
      lang === "ru"
        ? "Клиника не указана"
        : "Муассиса нишон дода нашудааст",

    filters:
      lang === "ru" ? "Фильтры" : "Филтрҳо",

    allTypes:
      lang === "ru" ? "Все типы" : "Ҳамаи намудҳо",

    allStatuses:
      lang === "ru" ? "Все статусы" : "Ҳамаи ҳолатҳо",

    search:
      lang === "ru"
        ? "Поиск по извещениям"
        : "Ҷустуҷӯ дар хабарномаҳо",

    reset:
      lang === "ru" ? "Сбросить" : "Тоза кардан",

    noResults:
      lang === "ru"
        ? "По выбранным параметрам извещений не найдено"
        : "Аз рӯи параметрҳои интихобшуда хабарнома ёфт нашуд",
  };

  const typeClass = (type) => {
    if (type === "medical_error") return "medical-error";
    if (type === "infection") return "infection";
    if (type === "adverse_event") return "adverse-event";
    return "default";
  };

  return (
    <main className="nizom-incidents-page">
      <div className="nizom-incidents-container">
        <header className="nizom-incidents-header">
          <div>
            <div className="nizom-incidents-title-row">
              <span className="nizom-incidents-title-accent" />
              <h1>{tr.title}</h1>
            </div>
            <p className="nizom-incidents-subtitle">
              {lang === "ru"
                ? "Регистрация и просмотр экстренных извещений."
                : "Сабт ва тамошои хабарномаҳои фаврӣ."}
            </p>
          </div>

          {!isInspector && (
            <button
              type="button"
              className="nizom-incidents-create-button"
              onClick={() => navigate("/incidents/create")}
            >
              <span className="nizom-incidents-plus" aria-hidden="true">
                +
              </span>
              {tr.create}
            </button>
          )}
        </header>

        {data.length > 0 && (
          <section className="nizom-incidents-filters">
            <div className="nizom-incidents-filters-heading">
              <span className="nizom-incidents-filter-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 6h16M7 12h10M10 18h4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span>{tr.filters}</span>
            </div>

            <div className="nizom-incidents-filter-grid">
              <label className="nizom-incidents-filter-field">
                <span>{tr.type}</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">{tr.allTypes}</option>
                  {Object.entries(TYPE_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="nizom-incidents-filter-field">
                <span>{tr.status}</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{tr.allStatuses}</option>
                  {Object.entries(STATUS_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="nizom-incidents-filter-field search">
                <span>{tr.search}</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tr.search}
                />
              </label>

              {hasFilters && (
                <button
                  type="button"
                  className="nizom-incidents-reset-button"
                  onClick={clearFilters}
                >
                  {tr.reset}
                </button>
              )}
            </div>
          </section>
        )}

        {data.length === 0 ? (
          <section className="nizom-incidents-empty">
            <div className="nizom-incidents-empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path
                  d="M6 4h12a1 1 0 0 1 1 1v14H5V5a1 1 0 0 1 1-1z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M8 8h8M8 12h8M8 16h5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p>{tr.empty}</p>
          </section>
        ) : filteredData.length === 0 ? (
          <section className="nizom-incidents-empty">
            <div className="nizom-incidents-empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path
                  d="M4 5h16v14H4z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M8 9h8M8 13h5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p>{tr.noResults}</p>
          </section>
        ) : isInspector ? (
          <section className="nizom-incidents-table-card">
            <div className="nizom-incidents-table-wrap">
              <table className="nizom-incidents-table">
                <thead>
                  <tr>
                    <th>{tr.type}</th>
                    <th>{tr.clinic}</th>
                    <th>{tr.date}</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() =>
                        navigate(`/incidents/${item.id}`)
                      }
                      className={
                        item.viewed_by_inspector
                          ? ""
                          : "is-unread"
                      }
                    >
                      <td>
                        <span
                          className={`nizom-incident-type ${typeClass(
                            item.type
                          )}`}
                        >
                          <span className="nizom-incident-type-dot" />
                          {TYPE_LABELS[item.type]}
                        </span>
                      </td>

                      <td>
                        {item.clinic_name ||
                          item.clinic ||
                          tr.noClinic}
                      </td>

                      <td className="nizom-incident-date">
                        {formatDate(
                          item.created_at ||
                            item.createdAt
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <section className="nizom-incidents-list">
            {filteredData.map((item) => (
              <article
                key={item.id}
                onClick={() =>
                  navigate(`/incidents/${item.id}`)
                }
                className={`nizom-incident-card ${
                  item.status === "draft"
                    ? "is-draft"
                    : "is-sent"
                }`}
              >
                <div className="nizom-incident-card-main">
                  <span
                    className={`nizom-incident-type ${typeClass(
                      item.type
                    )}`}
                  >
                    <span className="nizom-incident-type-dot" />
                    {TYPE_LABELS[item.type]}
                  </span>

                  <div className="nizom-incident-card-meta">
                    <span>
                      {formatDate(
                        item.created_at ||
                          item.createdAt
                      )}
                    </span>
                  </div>
                </div>

                <span
                  className={`nizom-incident-status ${
                    item.status === "draft"
                      ? "draft"
                      : "sent"
                  }`}
                >
                  {STATUS_LABELS[item.status]}
                </span>

                <span
                  className="nizom-incident-card-arrow"
                  aria-hidden="true"
                >
                  ›
                </span>
              </article>
            ))}
          </section>
        )}
      </div>
    </main>
  );
}
